/**
 * Settings › Account › Models (task 1147).
 *
 * Report: `project/reports/innovate-account-llm-provider-keys-and-model-policy.md`.
 * Mockup: `project/artifacts/innovate-settings-ai-models-mockup.html` (titled
 * "AI Models" there; the page ships as "Models").
 *
 * The account's model policy in three sections:
 *   1. Providers & keys — whose key each Model Provider's calls use (Bring Your
 *      Own Keys), the provider's on/off for this account, the default provider.
 *   2. Allowed models — per provider, which catalog Models agents may use and
 *      which one is the provider default.
 *   3. This period — model spend split by who paid the provider.
 *
 * Reads go to `/api/account/models` (service task 1146) and need only an
 * account scope; changes are owner/admin. The browser learns that from
 * `innovate.fe.account.models` (task 1217; split off the role-admin +
 * role-owner `innovate.fe.account.manage`): without it the page is read-only
 * and says so.
 * The service enforces the same rule on every write.
 */
import PageHeader from '../components/layout/PageHeader';
import { ProvidersSection } from '../components/accountModels/ProvidersSection';
import { AllowedModelsSection } from '../components/accountModels/AllowedModelsSection';
import { ThisPeriodSection } from '../components/accountModels/ThisPeriodSection';
import { useAccountAllowedModels, useAccountModelProviders } from '../hooks/useAccountModels';
import { useFlag } from '../hooks/useFeatureFlags';

export default function AccountModelsPage() {
  // The account Models split off innovate.fe.account.manage (task 1217), same admin+ cohorts.
  const canManage = useFlag('innovate.fe.account.models');
  const providersQuery = useAccountModelProviders();
  const allowedQuery = useAccountAllowedModels();
  const providers = providersQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title="Models"
        description="Which Model Providers and Models this account's AI Services may use, whose key they call with, and the defaults new agents start on."
      />

      {!canManage && (
        <p
          className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300"
          data-testid="account-models-read-only"
        >
          Only account owners and admins can change these settings.
        </p>
      )}

      <div className="mt-6 space-y-6">
        {providersQuery.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="account-models-providers-loading">
            Loading providers…
          </p>
        ) : providersQuery.isError ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="account-models-providers-error">
            This account&apos;s Model Providers could not be loaded. Try again in a moment.
          </p>
        ) : (
          <ProvidersSection providers={providers} canManage={canManage} />
        )}

        {allowedQuery.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="account-models-allowed-loading">
            Loading Models…
          </p>
        ) : allowedQuery.isError ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="account-models-allowed-error">
            This account&apos;s allowed Models could not be loaded. Try again in a moment.
          </p>
        ) : (
          <AllowedModelsSection groups={allowedQuery.data ?? []} providers={providers} canManage={canManage} />
        )}

        <ThisPeriodSection />
      </div>
    </div>
  );
}
